import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useSelector } from 'react-redux'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type CellContext, type ColumnDef, type RowSelectionState, type SortingState } from '@tanstack/react-table'
import { Search } from 'lucide-react'

import NotetakerOverrideDialog, { overrideLabel } from './NotetakerOverrideDialog'

import styles from './MembersList.module.scss'
import sharedStyles from '@/pages/app/Enterprises/components/styles.module.scss'

import { isApiError } from '@/api'
import {
  EnterpriseMember,
  EnterpriseRole,
  GetEnterpriseMembersSortByEnum,
  GetEnterpriseMembersSortDirEnum,
} from '@/api/generated'
import { getEnterpriseMembers, removeUsersFromEnterprise, updateUserRole } from '@/api/internal'
import Button from '@/components/Button'
import DataTable from '@/components/DataTable'
import DialogWrapper from '@/components/Dialog'
import Input from '@/components/Input'
import Pagination from '@/components/Pagination'
import Select from '@/components/Select'
import { useToast } from '@/components/Toast/ToastContext'
import { ASSIGNABLE_ROLE_OPTIONS, ENTERPRISE_ROLE_OPTIONS } from '@/constants/enterprise'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import { selectIsInternalAdmin } from '@/store/features/user/userSlice'

const PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 300

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' })

interface MembersListProps {
  enterpriseId: string
  enterpriseName: string
}

// Map react-table SortingState (single column) to the backend's sort enums.
// Defaults to ``name asc`` when sorting is empty.
const sortingToParams = (
  sorting: SortingState
): { sort_by: GetEnterpriseMembersSortByEnum; sort_dir: GetEnterpriseMembersSortDirEnum } => {
  const first = sorting[0]
  if (!first) return { sort_by: 'name', sort_dir: 'asc' }
  return {
    sort_by: first.id as GetEnterpriseMembersSortByEnum,
    sort_dir: first.desc ? 'desc' : 'asc',
  }
}

const MembersList = ({ enterpriseId, enterpriseName }: MembersListProps) => {
  const { updateToast } = useToast()
  const queryClient = useQueryClient()
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const impersonating = useSyncExternalStore(subscribeImpersonation, getImpersonation)
  const canManageNotetaker = isInternalAdmin && !impersonating
  const [overrideMembers, setOverrideMembers] = useState<EnterpriseMember[] | null>(null)

  // UI state
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }])
  const [page, setPage] = useState(0)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [updatingEmail, setUpdatingEmail] = useState<string | null>(null)
  const [showRemoveDialog, setShowRemoveDialog] = useState(false)

  // Debounce the search text so each keystroke doesn't fire a query.
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(handle)
  }, [search])

  useEffect(() => {
    setPage(0)
  }, [debouncedSearch, sorting])

  useEffect(() => {
    setRowSelection({})
    setOverrideMembers(null)
  }, [enterpriseId, search, debouncedSearch, sorting, page, canManageNotetaker])

  const { sort_by, sort_dir } = sortingToParams(sorting)

  const { data, isLoading, isFetching, isPlaceholderData, error } = useQuery({
    queryKey: ['enterprise-members', enterpriseId, debouncedSearch, sort_by, sort_dir, page],
    queryFn: () =>
      getEnterpriseMembers(enterpriseId, {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        search: debouncedSearch,
        sort_by,
        sort_dir,
      }),
    placeholderData: keepPreviousData,
  })

  const members = useMemo(() => data?.data.members ?? [], [data])
  const total = data?.data.total ?? 0

  // Clamp ``page`` if removals (or a narrowed search) shrink ``total`` below
  // the current offset; otherwise the next fetch returns an empty slice and
  // the table looks broken until the user manually pages back.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1)
    setPage((prev) => Math.min(prev, lastPage))
  }, [total])

  // Selection is keyed by ``userId`` (see ``getRowId`` below). Only act on
  // members that are both selected AND present on the current page; the
  // displayed count derives from the same set.
  const selectedMembers = useMemo(() => members.filter((m) => rowSelection[m.userId]), [members, rowSelection])
  const selectedCount = selectedMembers.length
  const overrideUnavailable = isFetching || isPlaceholderData || !!error || search !== debouncedSearch
  const selectedOverrideUnavailable = selectedMembers.some((member) => member.notetakerEntitlement === undefined)

  const updateRole = useMutation({
    mutationFn: (input: { email: string; role: EnterpriseRole }) =>
      updateUserRole(enterpriseId, input.email, input.role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-members', enterpriseId] })
    },
  })

  const removeMembers = useMutation({
    mutationFn: (emails: string[]) => removeUsersFromEnterprise(enterpriseId, emails),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-members', enterpriseId] })
    },
  })

  const handleRoleChange = useCallback(
    (member: EnterpriseMember, newRole: EnterpriseRole) => {
      if (member.role === newRole) return
      if (!member.email) return

      setUpdatingEmail(member.email)
      updateRole.mutate(
        { email: member.email, role: newRole },
        {
          onSuccess: () => {
            updateToast(`Updated ${member.email} to ${newRole}`, 'success')
          },
          onError: (err) => {
            if (isApiError(err)) {
              updateToast(err.detail || 'Failed to update role', 'error')
            } else {
              updateToast('Failed to update role', 'error')
            }
          },
          onSettled: () => {
            setUpdatingEmail(null)
          },
        }
      )
    },
    [updateRole, updateToast]
  )

  const handleRemoveConfirm = () => {
    const emails = selectedMembers.map((m) => m.email).filter((e): e is string => !!e)
    if (emails.length === 0) return

    removeMembers.mutate(emails, {
      onSuccess: () => {
        updateToast(`Removed ${emails.length} user(s) from enterprise`, 'success')
        setRowSelection({})
        setShowRemoveDialog(false)
      },
      onError: (err) => {
        if (isApiError(err)) {
          updateToast(err.detail || err.message || 'Failed to remove users', 'error')
        } else {
          updateToast('Failed to remove users', 'error')
        }
      },
    })
  }

  const columns = useMemo<ColumnDef<EnterpriseMember, unknown>[]>(
    () => [
      {
        id: 'select',
        enableSorting: false,
        header: ({ table }) => (
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={table.getIsAllPageRowsSelected()}
            ref={(el) => {
              if (el) el.indeterminate = table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected()
            }}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
            aria-label="Select all rows on this page"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            aria-label={`Select ${row.original.email ?? row.original.userId}`}
          />
        ),
      },
      {
        id: 'email',
        accessorFn: (m) => m.email ?? '',
        header: 'Email',
        cell: ({ row }) => <span>{row.original.email ?? '—'}</span>,
      },
      {
        id: 'name',
        accessorFn: (m) => `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || '—',
        header: 'Name',
        cell: ({ getValue }) => <span>{getValue<string>()}</span>,
      },
      {
        id: 'joinedAt',
        accessorFn: (m) => m.joinedAt,
        header: 'Joined',
        cell: ({ row }) => {
          const joinedAt = row.original.joinedAt
          if (joinedAt == null) return <span>—</span>
          return <span>{DATE_FORMATTER.format(new Date(joinedAt * 1000))}</span>
        },
      },
      {
        id: 'role',
        enableSorting: false,
        header: 'Role',
        cell: ({ row }) => {
          const member = row.original
          if (member.role == null) return <span>—</span>
          const options = member.role === EnterpriseRole.Superadmin ? ENTERPRISE_ROLE_OPTIONS : ASSIGNABLE_ROLE_OPTIONS
          return (
            <Select
              value={member.role}
              options={options}
              onChange={(newRole) => handleRoleChange(member, newRole as EnterpriseRole)}
              isLoading={updatingEmail === member.email}
              isDisabled={member.role === EnterpriseRole.Superadmin || !member.email}
            />
          )
        },
      },
      ...(canManageNotetaker
        ? [
            {
              id: 'notetakerEntitlement',
              header: 'Notetaker override',
              enableSorting: false,
              cell: ({ row }: CellContext<EnterpriseMember, unknown>) =>
                overrideLabel(row.original.notetakerEntitlement),
            },
          ]
        : []),
    ],
    [updatingEmail, handleRoleChange, canManageNotetaker]
  )

  return (
    <div className={styles.membersSection}>
      {/* Header */}
      <div className={styles.membersHeader}>
        <span className={styles.membersTitle}>Members</span>
        <span className={styles.membersCount}>{total} members</span>
      </div>

      {canManageNotetaker && (
        <p className={sharedStyles.dialogDetailItem}>
          Edit Notetaker for selected members. Other members keep their current settings.
        </p>
      )}

      {/* Search */}
      <div className={styles.searchBar}>
        <Search className={styles.searchIcon} aria-hidden="true" />
        <Input
          type="text"
          value={search}
          onInput={setSearch}
          placeholder="Search by name or email"
          className={styles.searchInput}
        />
      </div>

      {/* Selection Actions */}
      {selectedCount > 0 && (
        <div className={styles.selectionBar}>
          <span className={styles.selectedCount}>{selectedCount} selected</span>
          <div className={styles.bulkActions}>
            {canManageNotetaker && (
              <Button
                title="Edit Notetaker"
                type="light"
                onClick={() => setOverrideMembers([...selectedMembers])}
                isDisabled={overrideUnavailable || selectedOverrideUnavailable}
              />
            )}
            <Button title="Remove Selected" onClick={() => setShowRemoveDialog(true)} type="light" />
            <Button title="Clear" onClick={() => setRowSelection({})} type="link" />
          </div>
        </div>
      )}

      {/* Error State */}
      {error && <div className={sharedStyles.errorState}>Failed to load members. Please try again.</div>}

      {/* Table */}
      <DataTable<EnterpriseMember>
        data={members}
        columns={columns}
        isLoading={isLoading}
        emptyMessage="No members in this enterprise"
        ariaLabel="Enterprise members"
        manualSorting
        manualPagination
        sorting={sorting}
        onSortingChange={setSorting}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        getRowId={(m) => m.userId}
      />

      {/* Pagination */}
      <div className={styles.paginationContainer}>
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => p + 1)}
        />
      </div>

      {canManageNotetaker && overrideMembers && (
        <NotetakerOverrideDialog
          enterpriseId={enterpriseId}
          enterpriseName={enterpriseName}
          members={overrideMembers}
          isDisabled={overrideUnavailable}
          close={() => setOverrideMembers(null)}
          onSaved={() => {
            setOverrideMembers(null)
            setRowSelection({})
          }}
        />
      )}

      {/* Remove Dialog */}
      <DialogWrapper
        isOpen={showRemoveDialog}
        title="Remove from Enterprise"
        subtitle={
          selectedMembers.length === 1
            ? `Are you sure you want to remove ${selectedMembers[0]?.email} from ${enterpriseName}?`
            : `Are you sure you want to remove ${selectedMembers.length} users from ${enterpriseName}?`
        }
        close={() => setShowRemoveDialog(false)}
      >
        <div className={sharedStyles.dialogActions}>
          <Button title="Cancel" onClick={() => setShowRemoveDialog(false)} type="light" />
          <Button
            title={selectedMembers.length === 1 ? 'Remove' : `Remove ${selectedMembers.length} users`}
            onClick={handleRemoveConfirm}
            type="dark"
            isLoading={removeMembers.isPending}
            isDisabled={removeMembers.isPending || selectedMembers.length === 0}
          />
        </div>
      </DialogWrapper>
    </div>
  )
}

export default MembersList
